/**
 * SecurityPasswordGateModal
 *
 * Asks for the admin password before opening the Security UI.
 * Used when the user clicks the セキュリティ nav item.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './SecurityPasswordGateModal.css';

interface SecurityPasswordGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  submitting?: boolean;
  /** Error message from API (e.g. invalid password) */
  apiError?: string;
}

export const SecurityPasswordGateModal: React.FC<SecurityPasswordGateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  submitting = false,
  apiError
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const displayError = apiError || error;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = password.trim();
    if (!trimmed) {
      setError('パスワードを入力してください。');
      return;
    }
    onConfirm(trimmed);
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="security-gate-overlay"
      onClick={(e) => e.target === e.currentTarget && !submitting && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="security-gate-title"
    >
      <div className="security-gate-box">
        <h2 id="security-gate-title" className="security-gate-title">
          セキュリティ機能を開く
        </h2>
        <p className="security-gate-intro">
          セキュリティ設定を開くには、管理者パスワードを入力してください。
        </p>
        <form onSubmit={handleSubmit} className="security-gate-form">
          <label htmlFor="security-gate-password" className="security-gate-label">
            パスワード
          </label>
          <input
            id="security-gate-password"
            type="password"
            className="security-gate-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoComplete="current-password"
            placeholder="パスワードを入力"
          />
          {displayError && <p className="security-gate-error" role="alert">{displayError}</p>}
          <div className="security-gate-actions">
            <button
              type="button"
              className="security-gate-btn security-gate-btn-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="security-gate-btn security-gate-btn-confirm"
              disabled={submitting}
            >
              {submitting ? '確認中...' : '開く'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
